import { cn, getVersionForClone } from '@qr-front/ui/utils'
import { ComponentProps, forwardRef, ReactElement } from 'react'

import { Link } from '@/i18n-routing'

import type { TQrTypeCardProps } from './QrTypeCardTypes'

const ContainerIcon = ({ children }: { children: ReactElement }) => (
	<div
		className={cn(
			'flex size-12 min-w-12 items-center justify-center rounded-8 transition-colors ease-in desktop:group-hover:text-white',
			getVersionForClone({
				base: 'bg-purple-8 text-blue desktop:group-hover:bg-blue [&_svg]:size-[26px]',
				clone1:
					'rounded-full text-green desktop:group-hover:bg-green [&_svg]:size-8',
			}),
		)}
	>
		{children}
	</div>
)

export type TQrTypeCardDummyProps = Pick<
	TQrTypeCardProps,
	'type' | 'centeredLayout' | 'className'
> &
	Omit<ComponentProps<typeof Link>, 'href' | 'type'> & {
		urlQuery?: string
	}

export const QrTypeCardDummy = forwardRef<
	HTMLAnchorElement,
	TQrTypeCardDummyProps
>(
	(
		{
			type: { name, description, icon, url, badge },
			centeredLayout,
			className,
			...props
		},
		ref,
	) => {
		return (
			<Link
				ref={ref}
				{...props}
				href={url + (props.urlQuery ?? '')}
				className={cn(
					'group relative flex w-full items-center gap-4 bg-white p-3 shadow-generator ring-2 ring-inset ring-transparent transition ease-in [-webkit-tap-highlight-color:transparent] focus:outline-none mobile:p-4 desktop:gap-6',
					centeredLayout &&
						'flex h-full desktop:min-h-[182px] desktop:flex-col desktop:items-center desktop:justify-start desktop:gap-4 desktop:px-4 desktop:py-6 desktop:text-center',
					getVersionForClone({
						base: 'rounded-8 hover:ring-blue',
						clone1: 'rounded-16 hover:ring-green',
					}),
					className,
				)}
			>
				{badge ? (
					<span
						className={cn(
							'leading-3 absolute -top-2 right-4 rounded-full px-2 py-1 text-[8px] font-semibold uppercase text-white',
							getVersionForClone({
								base: 'bg-[#4981FF]',
								clone1: 'bg-[#48BA75]',
							}),
						)}
					>
						{badge}
					</span>
				) : null}
				<ContainerIcon>{icon}</ContainerIcon>
				<div>
					<h4
						className={cn(
							'type-heading-4 text-left',
							centeredLayout ? 'desktop:text-center' : 'desktop:text-left',
						)}
					>
						{name}
					</h4>
					<p
						className={cn(
							'type-body-small text-left text-dark-grey',
							centeredLayout ? 'desktop:text-center' : 'desktop:text-left',
						)}
					>
						{description}
					</p>
				</div>
			</Link>
		)
	},
)

QrTypeCardDummy.displayName = 'QrTypeCardDummy'
